import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { palette, gradients, radius, typography } from './designTokens';

// The standard page frame: a tinted canvas, a gradient hero carrying the
// title, subtitle and the screen's primary actions, then the content.
//
// `dense` trims the hero for screens sized against the viewport — it shaves
// roughly 32px off the block: tighter padding, a smaller tile, and a title one
// step down. Opt-in, because the roomy hero is right for the list screens that
// make up most callers.
const PageShell = ({
  title,
  subtitle,
  badge,
  actions,
  topRight,
  children,
  variant = 'commerce',
  icon,
  dense = false,
}) => {
  const heroBg = gradients[variant] || gradients.hero;

  return (
    // The canvas is deliberately transparent rather than surfaceCanvas: the
    // app shell behind this paints a background texture, and a solid tint here
    // would cover it only as far down as the content reaches — a hard band
    // ending mid-page. Inheriting keeps one continuous background.
    <Box sx={{ p: { xs: 2, md: 3 }, pb: 6, minHeight: '100%' }}>
      {topRight && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          {topRight}
        </Stack>
      )}
      <Box
        sx={{
          borderRadius: `${radius.lg}px`,
          background: heroBg,
          px: dense ? { xs: 2, md: 3 } : { xs: 2.5, md: 4 },
          py: dense ? { xs: 1.75, md: 2 } : { xs: 2.5, md: 3 },
          mb: dense ? 2 : 3,
          border: `1px solid ${palette.line}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          {/* The badge is an overline for the whole row, not the first line of
              the text column. Inside the column it made that column taller than
              the icon beside it, and since the icon centres against the column
              it sank below the badge and stopped lining up with the title. */}
          <Box sx={{ minWidth: 0 }}>
            {badge && (
              <Box
                sx={{
                  display: 'inline-block',
                  px: 1, py: 0.25, mb: 1.25,
                  bgcolor: 'rgba(255,255,255,0.7)',
                  borderRadius: '999px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: palette.brand,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                {badge}
              </Box>
            )}
            <Stack direction="row" alignItems="center" spacing={2}>
              {icon && (
                <Box
                  sx={{
                    // Never squashed by a long title on a narrow screen — a
                    // flex item with no basis will happily compress to an oval.
                    flexShrink: 0,
                    width: dense ? { xs: 40, md: 44 } : { xs: 48, md: 56 },
                    height: dense ? { xs: 40, md: 44 } : { xs: 48, md: 56 },
                    borderRadius: `${radius.lg}px`,
                    // Filled with the brand gradient rather than white: the hero
                    // behind it is #FFE9EA, so a white tile had almost no
                    // contrast and read as a smudge instead of a mark.
                    background: gradients.brand,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    boxShadow: '0 8px 20px rgba(241, 73, 80, 0.28)',
                  }}
                >
                  {icon}
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={dense ? { ...typography.pageTitle, fontSize: 20 } : typography.pageTitle}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography
                    sx={{
                      ...typography.body,
                      ...(dense && { fontSize: 13 }),
                      mt: 0.5,
                      maxWidth: 640,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
          {actions && (
            <Stack
              // On mobile the actions stack full-width into a tidy column
              // instead of wrapping into a ragged "two-then-one" row; from sm
              // up they sit inline at their natural widths.
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              flexWrap="wrap"
              useFlexGap
              sx={{
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                '& > *': { width: { xs: '100%', sm: 'auto' } },
              }}
            >
              {actions}
            </Stack>
          )}
        </Stack>
      </Box>

      <Box>{children}</Box>
    </Box>
  );
};

export default PageShell;
